import type { HttpError } from '@/core/api/errors/HttpError'

export type BeforeRequestHook = (request: Request) => Request | Promise<Request>
export type AfterResponseHook = (
  request: Request,
  response: Response,
) => Response | Promise<Response>

export interface HttpClientConfig {
  baseUrl: string
  timeout: number
  credentials: RequestCredentials
  headers: Record<string, string>
  hooks: {
    beforeRequest: BeforeRequestHook[]
    afterResponse: AfterResponseHook[]
  }
}

export class HttpClient {
  private readonly config: HttpClientConfig

  constructor(config: HttpClientConfig) {
    this.config = config
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>(path, 'GET')
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, 'POST', body)
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, 'PUT', body)
  }

  private async request<T>(path: string, method: string, body?: unknown): Promise<T> {
    const base = this.config.baseUrl.endsWith('/')
      ? this.config.baseUrl
      : `${this.config.baseUrl}/`
    const cleanPath = path.startsWith('/') ? path.slice(1) : path
    const url = base + cleanPath

    const initHeaders: Record<string, string> = { ...this.config.headers }
    if (body !== undefined) {
      initHeaders['Content-Type'] = 'application/json'
    }

    let request = new Request(url, {
      method,
      credentials: this.config.credentials,
      headers: initHeaders,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      signal: AbortSignal.timeout(this.config.timeout),
    })

    for (const hook of this.config.hooks.beforeRequest) {
      request = await hook(request)
    }

    let response = await fetch(request)

    for (const hook of this.config.hooks.afterResponse) {
      response = await hook(request, response)
    }

    return response.json() as Promise<T>
  }
}

export function createHttpClient(config: HttpClientConfig): HttpClient {
  return new HttpClient(config)
}

export type { HttpError }
